import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroCanvas from './HeroCanvas';
import { useLang } from '../../../context/LanguageContext';

export default function HeroBanner() {
  const sectionRef = useRef<HTMLElement>(null);
  const bgImgRef = useRef<HTMLImageElement>(null);
  const { t } = useLang();

  const scrollDown = () => {
    const next = sectionRef.current?.nextElementSibling;
    next?.scrollIntoView({ behavior: 'smooth' });
  };

  // Parallax: move bg image at 30% of scroll speed (GPU-friendly with transform)
  useEffect(() => {
    const handleScroll = () => {
      if (!bgImgRef.current) return;
      const y = window.scrollY * 0.28;
      bgImgRef.current.style.transform = `translateY(${y}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="always-dark relative w-full h-screen min-h-[680px] flex items-center justify-center overflow-hidden"
      style={{ background: '#060606' }}
    >
      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-14px); }
        }
        @keyframes glowFade {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%       { opacity: 1;    transform: scale(1.18); }
        }
        @keyframes orbMove1 {
          0%,100% { transform: translate(0px, 0px) scale(1); }
          33%     { transform: translate(70px, -60px) scale(1.1); }
          66%     { transform: translate(-40px, 40px) scale(0.94); }
        }
        @keyframes orbMove2 {
          0%,100% { transform: translate(0px, 0px) scale(1); }
          40%     { transform: translate(-80px, 60px) scale(1.12); }
          70%     { transform: translate(50px, -40px) scale(0.92); }
        }
        @keyframes scanSlide {
          0%   { transform: translateY(-100%); opacity: 0.6; }
          80%  { opacity: 0.15; }
          100% { transform: translateY(10000%); opacity: 0; }
        }
        @keyframes scrollPulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50%      { transform: scale(1.6); opacity: 0; }
        }
        @keyframes arrowBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(6px); }
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 0.85; }
        }
        @keyframes bgZoomIn {
          0%   { transform: translateY(0) scale(1.10); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 0.65; }
        }
        @keyframes gridFadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes logoEnter {
          0%   { opacity: 0; transform: scale(0.35) rotate(-8deg); filter: blur(12px); }
          55%  { opacity: 1; transform: scale(1.12) rotate(2deg); filter: blur(0px); }
          75%  { transform: scale(0.96) rotate(-1deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes eyebrowReveal {
          0%   { opacity: 0; transform: translateY(12px); letter-spacing: 0.05em; }
          100% { opacity: 1; transform: translateY(0); letter-spacing: 0.35em; }
        }
        @keyframes titleClipReveal {
          0%   { clip-path: inset(100% 0 0 0); transform: translateY(20px); opacity: 0.3; }
          100% { clip-path: inset(0% 0 0 0);   transform: translateY(0);    opacity: 1; }
        }
        @keyframes subtitleReveal {
          0%   { opacity: 0; transform: translateY(16px); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0px); }
        }
        @keyframes ctaSlideUp {
          0%   { opacity: 0; transform: translateY(28px) scale(0.94); }
          60%  { transform: translateY(-4px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scrollReveal {
          0%   { opacity: 0; transform: translateX(-50%) translateY(16px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        .hero-bg-enter {
          animation: bgZoomIn 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
          will-change: transform, opacity;
        }
        .grid-enter {
          animation: gridFadeIn 1.2s ease-out 0.8s both;
        }
        .orb-1 {
          animation: orbMove1 18s ease-in-out infinite;
        }
        .orb-2 {
          animation: orbMove2 24s ease-in-out infinite;
        }
        .logo-enter {
          animation: logoEnter 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s both;
          will-change: transform, opacity, filter;
        }
        .logo-float {
          animation: logoFloat 5s ease-in-out 1.7s infinite;
          will-change: transform;
        }
        .glow-loop {
          animation: glowFade 4s ease-in-out 1.95s infinite;
          will-change: opacity, transform;
        }
        .eyebrow-reveal {
          animation: eyebrowReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) 1.2s both;
        }
        .title-line-1 {
          animation: titleClipReveal 0.75s cubic-bezier(0.22, 1, 0.36, 1) 1.45s both;
          display: inline-block;
        }
        .title-line-2 {
          animation: titleClipReveal 0.75s cubic-bezier(0.22, 1, 0.36, 1) 1.65s both;
          display: inline-block;
        }
        .subtitle-reveal {
          animation: subtitleReveal 0.8s cubic-bezier(0.22, 1, 0.36, 1) 1.85s both;
        }
        .cta-1 {
          animation: ctaSlideUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 2.05s both;
        }
        .cta-2 {
          animation: ctaSlideUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 2.2s both;
        }
        .scroll-reveal {
          animation: scrollReveal 0.8s cubic-bezier(0.22, 1, 0.36, 1) 2.5s both;
        }
        .hero-scan { animation: scanSlide 5s linear 2.6s infinite; will-change: transform; }
        .grid-pulse { animation: gridPulse 4s ease-in-out infinite; }
        .scroll-ring-pulse { animation: scrollPulse 2.2s ease-out infinite; }
        .scroll-arrow { animation: arrowBounce 1.8s ease-in-out infinite; }
        .hero-bg-parallax { will-change: transform; }
      `}</style>

      {/* ── Background image with parallax ── */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <img
          ref={bgImgRef}
          src="https://readdy.ai/api/search-image?query=dark%20moody%20gaming%20pc%20room%20setup%20dramatic%20rgb%20red%20accent%20lighting%20minimal%20black%20background%20ultra%20detailed%20cinematic%20atmosphere%20technology%20hardware%20professional%20photography%20with%20dark%20shadows%20and%20red%20glowing%20highlights%20depth%20of%20field%20dark%20atmospheric&width=1920&height=1080&seq=hero-bg-spartech-v3&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="hero-bg-enter hero-bg-parallax absolute inset-0 w-full object-cover object-center"
          style={{ top: '-10%', height: '120%' }}
          loading="eager"
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(6,6,6,0.60) 0%, rgba(16,2,4,0.44) 45%, rgba(6,6,6,0.58) 100%)'
        }} />
        <div className="absolute bottom-0 left-0 right-0 h-40"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(6,6,6,0.95))' }} />
      </div>

      {/* ── Animated dot grid ── */}
      <div
        className="grid-pulse grid-enter absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(220,38,38,0.18) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
        }}
      />

      {/* ── Horizontal line grid ── */}
      <div
        className="grid-enter absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(220,38,38,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(220,38,38,0.04) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          zIndex: 1,
        }}
      />

      {/* ── Reduced orbs ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" style={{ zIndex: 1 }}>
        <div className="orb-1 absolute"
          style={{
            left: '3%', bottom: '8%', width: '640px', height: '640px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(220,38,38,0.50) 0%, rgba(220,38,38,0.22) 30%, rgba(220,38,38,0.06) 58%, transparent 72%)',
          }}
        />
        <div className="orb-2 absolute"
          style={{
            right: '2%', top: '4%', width: '720px', height: '720px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(185,28,28,0.42) 0%, rgba(185,28,28,0.16) 35%, rgba(185,28,28,0.04) 58%, transparent 72%)',
          }}
        />
      </div>

      {/* ── Particle canvas ── */}
      <div className="absolute inset-0" style={{ zIndex: 2 }}>
        <HeroCanvas />
      </div>

      {/* ── Scan line ── */}
      <div
        className="hero-scan absolute left-0 right-0 pointer-events-none"
        style={{
          top: 0, height: '2px', zIndex: 2,
          background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.8), rgba(255,255,255,0.3), rgba(220,38,38,0.8), transparent)',
        }}
      />

      {/* ── Content ── */}
      <div className="relative flex flex-col items-center text-center px-6 w-full max-w-4xl mx-auto" style={{ zIndex: 3 }}>
        {/* Logo with glow + float */}
        <div className="logo-float mb-8 relative flex items-center justify-center">
          <div
            className="glow-loop absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(220,38,38,0.85) 0%, transparent 65%)',
              filter: 'blur(30px)',
              margin: '-22px',
            }}
          />
          <img
            src="https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad"
            alt="SpartechRD"
            className="logo-enter relative w-20 h-20 sm:w-28 sm:h-28 md:w-40 md:h-40 object-contain drop-shadow-2xl"
            fetchpriority="high"
            loading="eager"
          />
        </div>

        {/* Eyebrow */}
        <div className="eyebrow-reveal flex items-center gap-3 mb-4">
          <div className="h-px bg-red-500/70" style={{ width: '40px' }} />
          <span className="text-red-400 text-[10px] font-extrabold uppercase tracking-[0.35em]">
            {t('hero_eyebrow')}
          </span>
          <div className="h-px bg-red-500/70" style={{ width: '40px' }} />
        </div>

        {/* Title */}
        <h1 className="text-white text-2xl sm:text-4xl md:text-6xl font-extrabold uppercase tracking-widest mb-4 leading-tight drop-shadow-2xl overflow-hidden">
          <span className="title-line-1 block">{t('hero_title1')}</span>
          <span className="title-line-2 block text-red-500">{t('hero_title2')}</span>
        </h1>

        <p className="subtitle-reveal text-zinc-300 text-xs sm:text-sm md:text-base mb-8 max-w-md leading-relaxed px-2">
          {t('hero_subtitle')}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center w-full sm:w-auto px-4 sm:px-0">
          <Link
            to="/catalogo"
            className="cta-1 w-full sm:w-auto px-7 sm:px-9 py-3 sm:py-3.5 text-xs font-extrabold tracking-widest uppercase text-white rounded-sm cursor-pointer whitespace-nowrap bg-red-600 hover:bg-red-500 transition-colors duration-200 text-center"
          >
            {t('hero_cta1')}
          </Link>
          <a
            href="#cotizacion"
            onClick={(e) => { e.preventDefault(); document.querySelector('#cotizacion')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="cta-2 w-full sm:w-auto border-2 border-white/40 hover:border-white/80 text-white/80 hover:text-white px-7 sm:px-9 py-3 sm:py-3.5 text-xs font-extrabold tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap rounded-sm backdrop-blur-sm text-center"
          >
            {t('hero_cta2')}
          </a>
        </div>
      </div>

      {/* ── Scroll button ── */}
      <button
        onClick={scrollDown}
        style={{ zIndex: 3 }}
        className="scroll-reveal absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group"
        aria-label="Scroll down"
      >
        <span className="text-white/40 group-hover:text-white/70 text-[9px] uppercase tracking-[0.45em] transition-colors duration-300">
          {t('hero_scroll')}
        </span>
        <div className="relative w-11 h-11 flex items-center justify-center">
          <div className="scroll-ring-pulse absolute inset-0 rounded-full border border-red-500/50" />
          <div className="absolute inset-0 rounded-full border border-white/20 group-hover:border-white/40 transition-colors duration-300" />
          <div className="absolute inset-[6px] rounded-full bg-white/5 group-hover:bg-red-600/20 transition-colors duration-300" />
          <i className="scroll-arrow ri-arrow-down-s-line text-white/60 group-hover:text-white text-lg transition-colors duration-300 relative z-10" />
        </div>
      </button>
    </section>
  );
}